import { useDispatch, useSelector } from 'react-redux';
import { selectEventsData } from '../../../store/events';
import { useEffect, useState } from 'react';
import useImagesSrc from '../../../hooks/useImagesScr';
import { usePreloadImages } from '../../../hooks/usePreloadImages';
import { sendAnalyticsEvents } from '../../../analytics/common';
import { EVENTS } from '../../../analytics/events';
import {
	ActiveIcon,
	ContentWrapper,
	InactiveIcon,
	StyledItem,
	StyledItemTitle,
	StyledList,
	Wrapper,
} from '../../track/TrackContainer';
import { AppButton } from '../../../components';
import styled from 'styled-components';
import { setScore } from '../../../store/info';

const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const StyledImage = styled.div`
	aspect-ratio: 327/314;
	width: 100%;
	background: url('../assets/plans/headway-paywall.png') center no-repeat;
	background-size: contain;
	margin-bottom: 23px;
	@media screen and (max-height: 600px) {
		height: 220px;
		margin-bottom: 10px;
	}
`;

const StyledButtons = styled.div`
	margin-bottom: 24px;
	display: flex;
	justify-content: space-between;
	@media screen and (max-height: 600px) {
		margin-bottom: 16px;
	}
`;

const QuestionTitle = styled.div`
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font24};
	font-weight: ${(props) => props.theme.fontWeights.bold};
  line-height: ${(props) => props.theme.lineHeights.lineHeight36};
	letter-spacing: -0.12px;
	margin-bottom: ${({withImg}) => withImg ? '0px' : '16px'};
	${({ customStyles }) => customStyles};
`;

const NotSureText = styled.div`
	text-align: center;
	color: ${(props) => props.theme.colors.colorNotSureText};
	font-size: ${(props) => props.theme.fontSizes.font16};
	font-weight: ${(props) => props.theme.fontWeights.light};
	cursor: pointer;
`;

const WrapperTrivia = styled(Wrapper)`
  box-shadow: none;
`

const ContentWrapperTrivia = styled(ContentWrapper)`
  box-shadow: none;
  padding: 0px 0px 8px;
`

const StyledItemTrivia = styled(StyledItem)`
  min-height: 72px;
  padding: ${({optionWithImg}) => optionWithImg ? '8px 16px 8px 8px' : '0px 16px 0px 24px'};
	justify-content: space-between;
	max-height: 72px;
	> img {
		width: 56px;
		height: 56px;
	}
`

const StyledItemTitleTrivia = styled(StyledItemTitle)`
  font-size: ${(props) => props.theme.fontSizes.font16};
  font-weight: ${(props) => props.theme.fontWeights.light};
  line-height: normal;
  padding: ${({optionWithImg}) => optionWithImg ? '0 5px 0 16px' : '0 16px 0 0'};
	max-width: 224px;
`

const TriviaTrackContainer = ({
	nextPage,
	pageEvent,
	title,
	options,
	multiple = false,
	onContinueClick,
	onSingleClick,
	withImg = false,
  questionIndex
}) => {
  const dispatch = useDispatch();
	const eventsData = useSelector(selectEventsData);
	const [initEventSent, setInitEventStatus] = useState(false);
	const [chosenOptions, setChosenOptions] = useState([]);
	const { imagesSources } = useImagesSrc();

	usePreloadImages(nextPage ? imagesSources[nextPage] : null);

  const handleSetScore = (pageIndex, currentScore) => {
    currentScore && dispatch(setScore({ pageIndex: pageIndex, score: currentScore }));
  };

  const firstQuestionBtnClick = (questionIndex, option) => {
    setChosenOptions(option.value);
    onSingleClick(option.value);
   	handleSetScore(questionIndex, option.score);
  }

	useEffect(() => {
		if (initEventSent) return;
		sendAnalyticsEvents(EVENTS[pageEvent], eventsData);
		setInitEventStatus(true);
	}, [eventsData, initEventSent, pageEvent]);

	const renderOption = (item, i) => {
		const isActive = chosenOptions.includes(item.value);
		const handleClick = () => {
			if (isActive) {
				setChosenOptions(chosenOptions.filter((value) => value !== item.value));
			} else {
				if (multiple) {
					setChosenOptions([...chosenOptions, item.value]);
				} else {
					setChosenOptions(item.value);
					onSingleClick(item.value);
          questionIndex && handleSetScore(questionIndex, item.score)
				}
			}
		};
		return (
			<StyledItemTrivia
				active={isActive}
				key={item.id}
				onClick={handleClick}
				id={`option-${i + 1}`}
				optionWithImg={item.imgPath}
			>
				{item.imgPath && <img src={item.imgPath} alt={item.name} />}
				<StyledItemTitleTrivia active={isActive} multiple={multiple} optionWithImg={item.imgPath}>
					{item.name}
				</StyledItemTitleTrivia>
				{multiple && (isActive ? ActiveIcon : InactiveIcon)}
			</StyledItemTrivia>
		);
	};

	return (
		<>
			<WrapperTrivia isMultiple={multiple} id="scrollable-div">
				<ContentWrapperTrivia>
					{withImg && <StyledImage />}
					<QuestionTitle
						withImg={withImg}
						customStyles={`text-align: ${
							withImg ? 'center' : textAlign
						}; word-break: break-word;`}
					>
						{title}
					</QuestionTitle>
					{withImg ? (
						<>
							<StyledButtons>
								{options.slice(0, 2).map((option, index) => (
									<AppButton
										key={index}
										displayStubBtn={false}
										onClick={() => firstQuestionBtnClick(questionIndex, option)}
										customWidth={'150px'}
										customMargin={'10px 0 0'}
									>
										{option.name}
									</AppButton>
								))}
							</StyledButtons>
							<NotSureText
								onClick={() => firstQuestionBtnClick(questionIndex, options[2])}
							>
								{options[2].name}
							</NotSureText>
						</>
					) : (
						<StyledList>{options.map(renderOption)}</StyledList>
					)}
				</ContentWrapperTrivia>
			</WrapperTrivia>
			{multiple && (
				<AppButton
					onClick={() => onContinueClick(chosenOptions)}
					bottomPosition={'36px'}
					disabled={!chosenOptions.length}
				/>
			)}
		</>
	);
};

export default TriviaTrackContainer;
